import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { getPortalContracts, getPortalContractFile } from "../../lib/api";

export default function PortalContracts() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getPortalContracts()
      .then((r) => setStudents(r.data?.students || []))
      .catch(() => toast.error("Failed to load contracts"))
      .finally(() => setLoading(false));
  }, []);

  const openFile = async (file, download) => {
    setBusy(file.id);
    try {
      const res = await getPortalContractFile(file.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      if (download) {
        a.download = file.filename || "contract";
      } else {
        a.target = "_blank";
        a.rel = "noopener noreferrer";
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error("Could not open that document");
    } finally { setBusy(null); }
  };

  const withContracts = students.filter((s) => (s.files || []).length > 0);

  if (loading) {
    return <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-contracts-loading">Loading…</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="portal-contracts">
      {withContracts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-contracts-empty">
            No contracts have been shared for your students yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="portal-contracts-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3">Student</th>
                  <th className="p-3">Student #</th>
                  <th className="p-3">Contract</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {withContracts.map((s) =>
                  s.files.map((f, i) => (
                    <tr key={f.id} className="border-b hover:bg-slate-50" data-testid={`portal-contract-row-${f.id}`}>
                      <td className="p-3 font-medium">{i === 0 ? s.student_name : ""}</td>
                      <td className="p-3 text-muted-foreground">{i === 0 ? (s.student_number || "—") : ""}</td>
                      <td className="p-3">
                        <span className="truncate">{f.filename}</span>
                        {f.uploaded_at && (
                          <span className="block text-xs text-muted-foreground">
                            {new Date(f.uploaded_at).toLocaleDateString()}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-blue-600" disabled={busy === f.id} onClick={() => openFile(f, false)} data-testid={`portal-contract-view-${f.id}`}>
                          View
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy === f.id} onClick={() => openFile(f, true)} data-testid={`portal-contract-dl-${f.id}`}>
                          {busy === f.id ? "…" : "Download"}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
